import {Helmet} from '@dr.pogodin/react-helmet';
import {DataStatus} from 'platform/components';

import {isNil} from 'ramda';
import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {useGetVehicleQuery, useUpdateVehicleMutation} from '@dms/api/vehicle';
import {useCreateServiceVehicleMutation} from '@dms/api/workshop';
import {ServiceVehicleForm} from '@dms/features/service-vehicle-form';
import {
  ServiceVehicle,
  VehicleForm as VFKit,
  ServiceVehicleValidationSchema,
  useAdditionalNote,
  toAPIVehicle,
  useGetCarFeatures,
  useFormNavigate,
  toBaseVehicle,
  SERVICE_INTERVALS_DEFAULT_VALUES,
  useVehicleDocumentSync,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {formatDocumentTitle, handleApiError} from '@dms/shared';

import {useRequiredParams} from 'shared/web';

export function CreateServiceFromSaleVehicle() {
  const {navigateOnDiscard, navigateOnConfirm} = useFormNavigate();
  const [updateVehicleMutation, {isLoading: isVehicleUpdating}] = useUpdateVehicleMutation();

  const {id: vehicleId} = useRequiredParams();

  // const {
  //   generateSchema,
  //   isMandatoryFieldsEnabled,
  //   isLoading: isMandatoryFieldsLoading,
  // } = useMandatoryFields();

  const {
    data: vehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery(
    {vehicleId},
    {
      skip: isNilOrEmpty(vehicleId),
    }
  );

  const [createServiceVehicle, {isLoading: isServiceVehicleCreating}] =
    useCreateServiceVehicleMutation();
  const {handleSaveNote} = useAdditionalNote();
  const [getCarFeatures] = useGetCarFeatures();
  const {syncDocuments} = useVehicleDocumentSync();

  // const [formSchema, setFormSchema] = useState<ZodSchema<ServiceVehicle>>(() =>
  //   generateSchema(ServiceVehicleValidationSchema, 'VEHICLETYPE_PASSENGER_CAR')
  // );

  // const handleVehicleTypeChange = useCallback(
  //   (type: VehicleTypeEnum) => {
  //     const newSchema = generateSchema(ServiceVehicleValidationSchema, type);
  //     setFormSchema(newSchema);
  //   },
  //   [generateSchema]
  // );

  const handleSave = async (values: ServiceVehicle) => {
    const sourcingData = await getCarFeatures(values);
    const existingFeatures = vehicle?.features || null;
    const apiPayload = toAPIVehicle(values, existingFeatures, sourcingData);

    return (
      updateVehicleMutation({
        vehicleId,
        updateVehicleRequestBody: apiPayload,
        validateMandatoryFields: false,
      })
        .unwrap()
        // Create service vehicle
        .then(async () => {
          await createServiceVehicle({
            vehicleId,
            vehicleClassId: values.vehicleClassId ?? undefined,
          }).unwrap();
        })
        // Handle additional note
        .then(async () => {
          if (values.additionalNote) {
            await handleSaveNote(vehicleId, values.additionalNote);
          }
        })
        // Sync battery certificate document
        .then(async () => {
          await syncDocuments({
            vehicleId,
            contextTarget: 'vehicle',
            fileFields: [
              {
                fieldValue: values.batteryCertificateDocument,
                documentNote: i18n.t('entity.vehicle.labels.batteryCertificate'),
              },
            ],
          });
        })
        // Navigate to vehicle detail
        .then(() => {
          navigateOnConfirm(vehicleId);
        })
        .catch(handleApiError)
    );
  };

  const isCreating = isVehicleUpdating || isServiceVehicleCreating;
  const isLoading = /* isMandatoryFieldsLoading || */ isVehicleLoading;
  const isError = isVehicleError;
  const baseVehicle = isNotNil(vehicle) ? toBaseVehicle(vehicle) : undefined;

  return (
    <>
      <Helmet>
        <title>
          {formatDocumentTitle(
            i18n.t('entity.vehicle.actions.createServiceForSaleVehicle'),
            i18n.t('entity.vehicle.labels.pageTitle')
          )}
        </title>
      </Helmet>
      <DataStatus
        isLoading={isLoading}
        isError={isError}
        isEmpty={isNil(baseVehicle)}
        emptyMessage={i18n.t('page.vehicle.notifications.notFound')}
        action={{
          title: i18n.t('general.actions.back'),
          leftIcon: 'navigation/chevron_left',
          onClick: navigateOnDiscard,
        }}
        data-testid="createServiceFromSaleVehicle-notFound"
      >
        <VFKit.Form
          mode="onBlur"
          experimentalZodSchema={ServiceVehicleValidationSchema}
          defaultValues={{
            ...baseVehicle,
            serviceIntervals: {
              ...SERVICE_INTERVALS_DEFAULT_VALUES,
              ...vehicle?.serviceIntervals,
            },
          }}
        >
          <ServiceVehicleForm
            onSave={handleSave}
            onDiscard={navigateOnDiscard}
            isCreating={isCreating}
            vehicleId={vehicleId}
            modalKeys={{
              headline: i18n.t('entity.vehicle.actions.createServiceForSaleVehicle'),
            }}
            tabRoutePattern="createServiceFromSaleVehicleV2Tab"
            disableVinDecoder
            infoAlert={{
              title: i18n.t('entity.vehicle.labels.creatingServiceForSaleVehicleMessage'),
              message: i18n.t('entity.vehicle.labels.creatingServiceForSaleVehicle'),
            }}
          />
        </VFKit.Form>
      </DataStatus>
    </>
  );
}
